/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 23/11/2023 | Lic. José Luis Aragón Cervantes  | Creación
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import { useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, InputAdornment, InputLabel, Paper, Tooltip, Typography, Box } from "@mui/material";
import { Link } from "react-router-dom";
import { routeTenantAdminPasswordRecovery } from "../../settings/routeConfig";

import KeyIcon from "@mui/icons-material/LockOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlineSharp";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";

import LogoMain from "../../img/sizesoftx-logo-1-20.svg";
import LogoSizeSoftX from "../../img/SIZESOFTX_LOGO1.png";
import CambioContrasenia from "./CambioContrasenia";

import { useSesion } from "../../context/System/SesionContext";
import TenantProfileSelector from "../tenant/TenantProfileSelector";
import Loader from "../utils/Loader";
import SizeSoftXAlert from "../utils/SizeSoftXAlert"

export default function Login() {
  const {
      sesionForm,
      handleChangeLoginForm,
      handleSubmitLoginForm,
      handleClickCambiarPassword,
      handleSubmitTenantAdminLogin,
      getTenantAdminLoginDefaults
  } = useSesion();

  const [openTenantAdminLogin, setOpenTenantAdminLogin] = useState(false);
  const [tenantAdminLoginForm, setTenantAdminLoginForm] = useState({
      email: "",
      password: "",
      mfaCode: "",
  });
  const [tenantAdminLoginMessage, setTenantAdminLoginMessage] = useState("");
  const [tenantAdminLoginLoading, setTenantAdminLoginLoading] = useState(false);

  const handleOpenTenantAdminLogin = async () => {
      setTenantAdminLoginMessage("");
      const defaults = await getTenantAdminLoginDefaults?.();
      setTenantAdminLoginForm({
          email: defaults?.email ?? "",
          password: "",
          mfaCode: "",
      });
      setOpenTenantAdminLogin(true);

      if (defaults && !defaults.hasLocalDevice) {
          setTenantAdminLoginMessage("No se encontro la licencia local del equipo. Abre PrinterTools y reintenta.");
      }
  };

  const handleCloseTenantAdminLogin = () => {
      if (tenantAdminLoginLoading) return;
      setOpenTenantAdminLogin(false);
      setTenantAdminLoginMessage("");
  };

  const handleChangeTenantAdminLogin = (event) => {
      const { name, value } = event.target;
      setTenantAdminLoginForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmitTenantAdminAccess = async (event) => {
      event.preventDefault();
      setTenantAdminLoginMessage("");

      if (!tenantAdminLoginForm.email.trim()) {
          setTenantAdminLoginMessage("Captura el correo administrador.");
          return;
      }

      if (!tenantAdminLoginForm.password.trim() && !tenantAdminLoginForm.mfaCode.trim()) {
          setTenantAdminLoginMessage("Captura password administrador o codigo Google Authenticator.");
          return;
      }

      setTenantAdminLoginLoading(true);
      const result = await handleSubmitTenantAdminLogin?.({
          email: tenantAdminLoginForm.email,
          password: tenantAdminLoginForm.password,
          mfaCode: tenantAdminLoginForm.mfaCode,
      });
      setTenantAdminLoginLoading(false);

      if (!result?.ok) {
          setTenantAdminLoginMessage(result?.message ?? "No fue posible validar el acceso administrativo.");
      }
  };

  return (
      <div className="login-container">
          <Tooltip title="Administración Licencia SieSoftX">
              <IconButton
                  onClick={handleOpenTenantAdminLogin}
                  size="small"
                  sx={{
                      position: "fixed",
                      top: 12,
                      left: 12,
                      zIndex: 10,
                      bgcolor: "rgba(0,0,0,0.18)",
                      color: "#FFFFFF",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.32)" },
                  }}
              >
                  <AdminPanelSettingsOutlinedIcon />
              </IconButton>
          </Tooltip>
          <Grid container spacing={2} justifyContent="center" alignItems="center">
              <Grid item xs={12} md={6} className="login-content-container">
                  <Paper sx={{ padding: 6 }} className="login-content">
                      <form noValidate autoComplete="off" onSubmit={handleSubmitLoginForm}>
                          <Grid container>
                              <Grid item xs={12} align="center">
                                  <img src={LogoMain} alt="logo_main" style={{ maxWidth: 460 }} />
                              </Grid>
                              <Grid container sx={{ paddingTop: 3 }}>
                                  <Grid item xs={12}>
                                      <Typography style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '20px' }}>
                                          Iniciar sesión
                                      </Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                      <TenantProfileSelector />
                                  </Grid>
                                   <Grid item xs={12}>
                                       <InputLabel className="lbl" required>
                                           Usuario
                                       </InputLabel>
                                       <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                           <TextField
                                               name="username"
                                               value={sesionForm?.usuario.value}
                                               onChange={handleChangeLoginForm}
                                               error={sesionForm?.usuario.error}
                                               helperText={sesionForm?.usuario.TypeError}
                                               autoComplete="off" // Previene sugerencias de autocompletado estándar
                                               autoCorrect="off"
                                               autoCapitalize="off"
                                               inputMode="text"
                                               InputProps={{
                                                   startAdornment: (
                                                       <InputAdornment position="start">
                                                           <PersonIcon />
                                                       </InputAdornment>
                                                   ),
                                               }}
                                               sx={{ flex: 1 }}
                                           />
                                       </Box>
                                   </Grid>
                                   <Grid item xs={12}>
                                       <InputLabel className="lbl" required>
                                           Contraseña
                                       </InputLabel>
                                       <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                           <TextField
                                               name="password"
                                               value={sesionForm?.password.value}
                                               onChange={handleChangeLoginForm}
                                               error={sesionForm?.password.error}
                                               helperText={sesionForm?.password.TypeError}
                                               type="password"
                                               autoComplete="off" // Previene sugerencias de autocompletado estándar
                                               autoCorrect="off"
                                               autoCapitalize="off"
                                               inputMode="text"
                                               InputProps={{
                                                   startAdornment: (
                                                       <InputAdornment position="start">
                                                           <KeyIcon />
                                                       </InputAdornment>
                                                   ),
                                               }}
                                               sx={{ flex: 1 }}
                                           />
                                       </Box>
                                   </Grid>
                                  <Grid item xs={12} display="flex" justifyContent="flex-end">
                                      <Link onClick={handleClickCambiarPassword}>
                                          <Typography variant="body2">Olvidé mi contraseña</Typography>
                                      </Link>
                                  </Grid>
                                  <Grid item xs={12} textAlign="center">
                                      <Button type="submit" style={{ backgroundColor: '#a9701e', color: '#FFFFFF' }} fullWidth>
                                          Entrar
                                      </Button>
                                  </Grid>
                                  <Grid item xs={12} className="center">
                                      <Typography variant="caption">
                                          &copy; {new Date().getFullYear()} - Sistema para el control de ventas e inventario en su modalidad Ropa y calzado (Con soporte para tienda general)
                                      </Typography>
                                  </Grid>
                              </Grid>
                          </Grid>
                      </form>
                  </Paper>
              </Grid>
              <Grid item xs={12} md={6} className="imagen-container">
                  <div className="background-image">
                      <img src={LogoSizeSoftX} alt="login_main" className="imagen-ocupar-pantalla" />
                  </div>
              </Grid>
          </Grid>
          <SizeSoftXAlert />
          <CambioContrasenia />
          <Loader />
          <Dialog open={openTenantAdminLogin} onClose={handleCloseTenantAdminLogin} maxWidth="xs" fullWidth>
              <form noValidate onSubmit={handleSubmitTenantAdminAccess}>
                  <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: "bold" }}>
                      <AdminPanelSettingsOutlinedIcon color="warning" />
                      Administracion ARSync
                  </DialogTitle>
                  <DialogContent>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                          Valida la cuenta administradora para abrir el panel de licencia y equipos.
                      </Typography>
                      {tenantAdminLoginMessage && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                              {tenantAdminLoginMessage}
                          </Alert>
                      )}
                      <TextField
                          name="email"
                          label="Correo administrador"
                          value={tenantAdminLoginForm.email}
                          onChange={handleChangeTenantAdminLogin}
                          fullWidth
                          margin="dense"
                          autoComplete="off"
                          InputProps={{
                              startAdornment: (
                                  <InputAdornment position="start">
                                      <PersonIcon />
                                  </InputAdornment>
                              ),
                          }}
                      />
                      <TextField
                          name="password"
                          label="Password dashboard"
                          value={tenantAdminLoginForm.password}
                          onChange={handleChangeTenantAdminLogin}
                          fullWidth
                          margin="dense"
                          type="password"
                          autoComplete="off"
                          InputProps={{
                              startAdornment: (
                                  <InputAdornment position="start">
                                      <KeyIcon />
                                  </InputAdornment>
                              ),
                          }}
                      />
                      <TextField autoComplete="off"
                          name="mfaCode"
                          label="Codigo Google Authenticator"
                          value={tenantAdminLoginForm.mfaCode}
                          onChange={handleChangeTenantAdminLogin}
                          fullWidth
                          margin="dense"
                          inputProps={{ inputMode: "numeric", maxLength: 6 }}
                          helperText="Puedes usar password o el codigo MFA registrado."
                      />
                      <Button component={Link} to={routeTenantAdminPasswordRecovery} size="small" sx={{ mt: 1 }}>
                          Recuperar password dashboard
                      </Button>
                  </DialogContent>
                  <DialogActions>
                      <Button onClick={handleCloseTenantAdminLogin} disabled={tenantAdminLoginLoading}>
                          Cancelar
                      </Button>
                      <Button type="submit" variant="contained" disabled={tenantAdminLoginLoading}>
                          Entrar
                      </Button>
                  </DialogActions>
              </form>
          </Dialog>
      </div>
  );
}
